import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Building2,
  Home,
  ClipboardList,
  CheckCircle,
  AlertCircle,
  Settings,
  Search,
  RefreshCw,
  Bell,
  Award,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import type {
  IDashboardStats,
  INotification,
  IAllotment,
  IBuilding,
  IFlat,
} from "../types/quarters.types";

const QuartersDashboard = () => {
  const navigate = useNavigate();
  const { apiRequest, isSuperAdmin } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);

  const [stats, setStats] = useState<IDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [myQuarters, setMyQuarters] = useState<IAllotment[]>([]);
  const [activeTab, setActiveTab] = useState<"super_admin" | "employee">("employee");

  // Fetch initial data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const statsRes = await quartersService.getDashboardStats();
      if (statsRes.success) {
        setStats(statsRes.data);
      }

      // Fetch my notifications
      const notifRes = await quartersService.getMyNotifications();
      if (notifRes.success) {
        setNotifications(notifRes.data);
      }

      // Fetch my quarters
      const myQuartersRes = await quartersService.getMyQuarters();
      if (myQuartersRes.success) {
        setMyQuarters(myQuartersRes.data);
      }

      if (isSuperAdmin) {
        setActiveTab("super_admin");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, quartersService]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkAsRead = async (notifId: string) => {
    try {
      await quartersService.markNotificationRead(notifId);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notifId ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <span className="text-slate-600 font-medium">Loading Quarters Portal...</span>
        </div>
      </div>
    );
  }

  const isPrivileged = isSuperAdmin;

  // SVG Chart Computations
  const flatStatus = stats?.flatStatus || { vacant: 0, occupied: 0, reserved: 0, maintenance: 0 };
  const totalFlatsCount =
    flatStatus.vacant + flatStatus.occupied + flatStatus.reserved + flatStatus.maintenance;

  const getPercentage = (count: number) => {
    if (!totalFlatsCount) return 0;
    return Math.round((count / totalFlatsCount) * 100);
  };

  // Pie chart variables
  const vacantPct = getPercentage(flatStatus.vacant);
  const occupiedPct = getPercentage(flatStatus.occupied);
  const reservedPct = getPercentage(flatStatus.reserved);
  const maintenancePct = getPercentage(flatStatus.maintenance);

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10 font-sans">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div>
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
            House Allotment Portal
          </span>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-2 text-slate-900">
            Accommodation & Quarters
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Dynamic, rule-based housing management system
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="bg-slate-50 hover:bg-slate-100 border border-slate-200 p-3 rounded-lg transition-all duration-200 text-slate-700"
            title="Refresh Data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {isPrivileged && (
            <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200">
              <button
                onClick={() => setActiveTab("employee")}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === "employee" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
              >
                Employee Portal
              </button>
              <button
                onClick={() => setActiveTab("super_admin")}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === "super_admin" ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
              >
                Admin Control
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {/* RENDER EMPLOYEE VIEW */}
      {activeTab === "employee" ? (
        <div className="space-y-8">
          {/* Summary Dashboard Overview Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-blue-100 text-blue-700 p-3 rounded-xl">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Buildings</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.buildings || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-indigo-100 text-indigo-700 p-3 rounded-xl">
                <Home className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Quarters</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.flats || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-emerald-100 text-emerald-700 p-3 rounded-xl">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Vacant Flats</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.flatStatus?.vacant || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-purple-100 text-purple-700 p-3 rounded-xl">
                <Home className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Occupied Flats</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.flatStatus?.occupied || 0}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Quick Actions & Allotted Quarter */}
            <div className="lg:col-span-2 space-y-8">
              {/* Quick Actions */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" /> Quick Actions
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => navigate("/quarters/apply")}
                    className="group bg-white hover:bg-slate-50 border border-slate-200 rounded-xl p-5 text-left transition-all hover:shadow-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="bg-blue-600 text-white p-3 rounded-xl">
                        <ClipboardList className="w-6 h-6" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="text-slate-800 font-bold mt-4">Apply for Allotment</h3>
                    <p className="text-slate-500 text-xs mt-1">
                      Submit details, service seniority, upload documents, and choose preference.
                    </p>
                  </button>

                  <button
                    onClick={() => navigate("/quarters/my-applications")}
                    className="group bg-white hover:bg-slate-50 border border-slate-200 rounded-xl p-5 text-left transition-all hover:shadow-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="bg-indigo-600 text-white p-3 rounded-xl">
                        <Search className="w-6 h-6" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="text-slate-800 font-bold mt-4">My Applications</h3>
                    <p className="text-slate-500 text-xs mt-1">
                      Track status, verification logs, document status, and waitlist ranking.
                    </p>
                  </button>

                  <button
                    onClick={() => navigate("/quarters/inventory")}
                    className="group bg-white hover:bg-slate-50 border border-slate-200 rounded-xl p-5 text-left transition-all hover:shadow-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div className="bg-emerald-600 text-white p-3 rounded-xl">
                        <Building2 className="w-6 h-6" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <h3 className="text-slate-800 font-bold mt-4">Browse Quarters & Vacancy</h3>
                    <p className="text-slate-500 text-xs mt-1">
                      View all buildings, flat inventory, and current vacancy status.
                    </p>
                  </button>
                </div>
              </div>

              {/* Current Housing Allotment */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                  <Home className="w-5 h-5 text-blue-600" /> My Current Quarters
                </h2>
                {myQuarters.length > 0 ? (
                  myQuarters.map((allotment) => (
                    <div
                      key={allotment._id}
                      className="border border-slate-100 rounded-2xl p-6 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-6"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-bold uppercase">
                            {allotment.status}
                          </span>
                          <span className="text-slate-500 text-sm font-medium">
                            Type: {typeof allotment.flatId === "object" && allotment.flatId
                              ? typeof (allotment.flatId as IFlat).buildingId === "object"
                                ? ((allotment.flatId as IFlat).buildingId as IBuilding).quarterType
                                : "—"
                              : "—"}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-800">
                          {(() => {
                            const f = typeof allotment.flatId === "object" ? allotment.flatId as IFlat : null;
                            const b = f && typeof f.buildingId === "object" ? f.buildingId as IBuilding : null;
                            return `Flat ${f?.flatNumber} - ${b?.name ?? ""}`;
                          })()}
                        </h3>
                        <p className="text-slate-500 text-xs">
                          {(() => {
                            const f = typeof allotment.flatId === "object" ? allotment.flatId as IFlat : null;
                            const b = f && typeof f.buildingId === "object" ? f.buildingId as IBuilding : null;
                            return `Campus: ${b?.campus ?? "Main Campus"} | Area: ${f?.area ?? "N/A"} Sq.ft`;
                          })()}
                        </p>
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div>
                            <p className="text-[10px] text-slate-400 font-semibold uppercase">Allotment Date</p>
                            <p className="text-slate-700 text-sm font-bold">
                              {new Date(allotment.allottedAt).toLocaleDateString()}
                            </p>
                          </div>
                          {allotment.vacatingDate && (
                            <div>
                              <p className="text-[10px] text-slate-400 font-semibold uppercase">Scheduled Vacate</p>
                              <p className="text-slate-700 text-sm font-bold">
                                {new Date(allotment.vacatingDate).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => {
                            const f = typeof allotment.flatId === "object" ? allotment.flatId as IFlat : null;
                            if (f) navigate(`/quarters/flat/${f._id}`);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-5 py-3 rounded-xl transition shadow-md shadow-blue-200 text-center"
                        >
                          View Flat Dashboard
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <Home className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium text-sm">No quarters allotted currently</p>
                    <p className="text-slate-400 text-xs mt-1">
                      Apply through the application wizard to enter the waitlist.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: In-App Notifications & Timeline */}
            <div className="space-y-8">
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-h-[500px] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-600" /> Notifications
                  </h2>
                  {notifications.filter((n) => !n.isRead).length > 0 && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-bold">
                      {notifications.filter((n) => !n.isRead).length} New
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  {notifications.length > 0 ? (
                    notifications.map((notif) => (
                      <div
                        key={notif._id}
                        onClick={() => !notif.isRead && handleMarkAsRead(notif._id)}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer ${notif.isRead
                            ? "bg-slate-50 border-slate-100 hover:bg-slate-100/50"
                            : "bg-blue-50/30 border-blue-100 hover:bg-blue-50/50 shadow-sm"
                          }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-bold ${notif.isRead ? "text-slate-700" : "text-blue-900"}`}>
                            {notif.title}
                          </p>
                          {!notif.isRead && <span className="w-2 h-2 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></span>}
                        </div>
                        <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">{notif.message}</p>
                        <p className="text-slate-400 text-[10px] mt-2 font-semibold">
                          {new Date(notif.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-xs">No notifications yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* RENDER ADMIN VIEW */
        <div className="space-y-8">
          {/* Admin KPI Panel */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-blue-100 text-blue-700 p-3 rounded-xl">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Buildings</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.buildings || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-indigo-100 text-indigo-700 p-3 rounded-xl">
                <Home className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Quarters</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.flats || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-emerald-100 text-emerald-700 p-3 rounded-xl">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Vacant Flats</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.flatStatus?.vacant || 0}</p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:border-slate-300 transition-colors">
              <div className="bg-orange-100 text-orange-700 p-3 rounded-xl">
                <ClipboardList className="w-6 h-6" />
              </div>
              <div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Pending Apps</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.applications?.submitted || 0}</p>
              </div>
            </div>
          </div>

          {/* Quick Links Menu */}
          <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm">
            <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" /> Administrative Operations
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <button
                onClick={() => navigate("/quarters/admin/buildings")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <Building2 className="w-6 h-6 text-blue-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Buildings</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/flats")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <Home className="w-6 h-6 text-indigo-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Quarters Inventory</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/applications")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <ClipboardList className="w-6 h-6 text-orange-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Applications</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/allotment-engine")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <Award className="w-6 h-6 text-emerald-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Allotment Engine</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/rules")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <Settings className="w-6 h-6 text-purple-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Scoring & Rules</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/reservations")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <Settings className="w-6 h-6 text-amber-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Reservations</span>
              </button>

              <button
                onClick={() => navigate("/quarters/admin/reports")}
                className="flex flex-col items-center justify-center p-4 border border-slate-100 bg-slate-50/50 hover:bg-blue-50/50 hover:border-blue-200 hover:shadow-sm rounded-xl text-center transition-all group"
              >
                <ClipboardList className="w-6 h-6 text-teal-600 group-hover:scale-105 transition-transform" />
                <span className="text-slate-700 text-xs font-bold mt-2">Reports & Logs</span>
              </button>
            </div>
          </div>

          {/* Visual Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Occupancy Chart */}
            <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-slate-800 font-bold text-base mb-1">Occupancy Status</h3>
                <p className="text-slate-400 text-xs">Current usage breakdown of total flats</p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-around gap-6 py-6">
                {/* SVG Donut Chart */}
                <div className="relative w-40 h-40 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    {/* Background Circle */}
                    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f1f5f9" strokeWidth="3" />

                    {/* Occupied Sector */}
                    <circle
                      cx="18"
                      cy="18"
                      r="15.915"
                      fill="none"
                      stroke="#4f46e5"
                      strokeWidth="3.2"
                      strokeDasharray={`${occupiedPct} ${100 - occupiedPct}`}
                      strokeDashoffset="0"
                    />

                    {/* Vacant Sector */}
                    <circle
                      cx="18"
                      cy="18"
                      r="15.915"
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3.2"
                      strokeDasharray={`${vacantPct} ${100 - vacantPct}`}
                      strokeDashoffset={-occupiedPct}
                    />

                    {/* Reserved Sector */}
                    <circle
                      cx="18"
                      cy="18"
                      r="15.915"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="3.2"
                      strokeDasharray={`${reservedPct} ${100 - reservedPct}`}
                      strokeDashoffset={-(occupiedPct + vacantPct)}
                    />

                    {/* Maintenance Sector */}
                    <circle
                      cx="18"
                      cy="18"
                      r="15.915"
                      fill="none"
                      stroke="#ef4444"
                      strokeWidth="3.2"
                      strokeDasharray={`${maintenancePct} ${100 - maintenancePct}`}
                      strokeDashoffset={-(occupiedPct + vacantPct + reservedPct)}
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center">
                    <span className="text-2xl font-black text-slate-800">{totalFlatsCount}</span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase">Total Flats</span>
                  </div>
                </div>

                {/* Legends */}
                <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
                  <div className="flex items-center gap-2.5">
                    <span className="w-3.5 h-3.5 bg-indigo-600 rounded-lg flex-shrink-0"></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Occupied</p>
                      <p className="text-sm font-bold text-slate-700">
                        {flatStatus.occupied} ({occupiedPct}%)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="w-3.5 h-3.5 bg-emerald-500 rounded-lg flex-shrink-0"></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Vacant</p>
                      <p className="text-sm font-bold text-slate-700">
                        {flatStatus.vacant} ({vacantPct}%)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="w-3.5 h-3.5 bg-amber-500 rounded-lg flex-shrink-0"></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Reserved</p>
                      <p className="text-sm font-bold text-slate-700">
                        {flatStatus.reserved} ({reservedPct}%)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="w-3.5 h-3.5 bg-red-500 rounded-lg flex-shrink-0"></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Maintenance</p>
                      <p className="text-sm font-bold text-slate-700">
                        {flatStatus.maintenance} ({maintenancePct}%)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quarter Type Distribution */}
            <div className="bg-white border border-slate-200 p-6 rounded-xl shadow-sm flex flex-col justify-between">
              <div>
                <h3 className="text-slate-800 font-bold text-base mb-1">Quarter Type Breakdown</h3>
                <p className="text-slate-400 text-xs">Quantity of flats classified by category</p>
              </div>

              <div className="space-y-4 py-4">
                {Object.entries(stats?.quarterTypes || {}).length > 0 ? (
                  Object.entries(stats?.quarterTypes || {}).map(([key, value]: any) => {
                    const maxVal = Math.max(...(Object.values(stats?.quarterTypes || {}) as number[]), 1);
                    const widthPct = Math.round((value / maxVal) * 100);

                    return (
                      <div key={key} className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs font-bold">
                          <span className="text-slate-600">{key}</span>
                          <span className="text-slate-800">{value} Flats</span>
                        </div>
                        <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                            style={{ width: `${widthPct}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-10">
                    <p className="text-slate-400 text-xs">No quarters configured yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuartersDashboard;
